import { useEffect, useMemo, useState } from "react";
import { ArrowDownCircle, ArrowUpCircle } from "lucide-react";
import api from "../api/axios";
import toast from "react-hot-toast";

export default function AdminTransactionsPage() {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const run = async () => {
      try {
        const { data } = await api.get("/admin/transactions");
        setTransactions(Array.isArray(data) ? data : []);
      } catch (error) {
        toast.error(error.response?.data?.message || "Failed to load transactions");
      } finally {
        setLoading(false);
      }
    };

    run();
  }, []);

  const tableRows = useMemo(
    () => transactions.filter((txn) => !txn.type?.startsWith("crypto_")).slice(0, 200),
    [transactions]
  );

  return (
    <div className="mx-auto max-w-7xl space-y-6 text-white">
      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-6 backdrop-blur-xl">
        <p className="text-sm uppercase tracking-[0.24em] text-cyan-300/80">Admin</p>
        <h1 className="mt-2 text-3xl font-bold text-white">Transactions</h1>
        <p className="mt-2 text-sm text-slate-400">
          Monitor transaction flow, statuses, and user-level payment history.
        </p>
      </section>

      <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Rows Loaded</p>
          <p className="mt-3 text-3xl font-bold text-white">{tableRows.length}</p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Successful</p>
          <p className="mt-3 text-3xl font-bold text-emerald-300">
            {tableRows.filter((txn) => txn.status === "success").length}
          </p>
        </div>
        <div className="rounded-[1.75rem] border border-white/10 bg-slate-900/75 p-5 backdrop-blur-xl">
          <p className="text-xs uppercase tracking-[0.2em] text-slate-400">Pending</p>
          <p className="mt-3 text-3xl font-bold text-amber-300">
            {tableRows.filter((txn) => txn.status === "pending").length}
          </p>
        </div>
      </section>

      <section className="rounded-[2rem] border border-white/10 bg-slate-900/75 p-4 md:p-6 backdrop-blur-xl">
        {loading && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-4 text-slate-300">
            Loading transactions...
          </div>
        )}

        {!loading && tableRows.length === 0 && (
          <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center text-slate-400">
            No transactions found.
          </div>
        )}

        {!loading && tableRows.length > 0 && (
          <>
            <div className="hidden overflow-x-auto md:block">
              <table className="min-w-full border-separate border-spacing-y-2 text-sm">
                <thead>
                  <tr className="text-left text-xs uppercase tracking-wider text-slate-400">
                    <th className="px-4 py-2 font-semibold">ID</th>
                    <th className="px-4 py-2 font-semibold">User</th>
                    <th className="px-4 py-2 font-semibold">Type</th>
                    <th className="px-4 py-2 font-semibold">Amount</th>
                    <th className="px-4 py-2 font-semibold">Status</th>
                    <th className="px-4 py-2 font-semibold">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {tableRows.map((txn) => (
                    <tr key={txn._id} className="bg-white/5 text-slate-200">
                      <td className="rounded-l-2xl px-4 py-4 text-slate-300">{txn.reference || txn._id.slice(-8)}</td>
                      <td className="px-4 py-4 text-white">{txn.wallet?.user?.fullName || "N/A"}</td>
                      <td className="px-4 py-4">
                        <div className="flex items-center gap-2 capitalize text-slate-300">
                          {txn.type === "deposit" ? (
                            <ArrowDownCircle className="text-emerald-400" size={18} />
                          ) : (
                            <ArrowUpCircle className="text-rose-400" size={18} />
                          )}
                          {txn.type.replace("_", " ")}
                        </div>
                      </td>
                      <td className="px-4 py-4 font-semibold text-white">₦{txn.amount.toLocaleString()}</td>
                      <td className="px-4 py-4">
                        <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                          txn.status === "success"
                            ? "bg-emerald-500/15 text-emerald-300"
                            : txn.status === "pending"
                            ? "bg-amber-500/15 text-amber-300"
                            : "bg-rose-500/15 text-rose-300"
                        }`}>
                          {txn.status}
                        </span>
                      </td>
                      <td className="rounded-r-2xl px-4 py-4 text-slate-400">{new Date(txn.createdAt).toLocaleString()}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="space-y-3 md:hidden">
              {tableRows.map((txn) => (
                <div key={txn._id} className="rounded-2xl border border-white/10 bg-white/5 p-4">
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-sm text-slate-400">{txn.reference || txn._id.slice(-8)}</p>
                      <p className="mt-1 font-semibold text-white">{txn.wallet?.user?.fullName || "N/A"}</p>
                    </div>
                    <span className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
                      txn.status === "success"
                        ? "bg-emerald-500/15 text-emerald-300"
                        : txn.status === "pending"
                        ? "bg-amber-500/15 text-amber-300"
                        : "bg-rose-500/15 text-rose-300"
                    }`}>
                      {txn.status}
                    </span>
                  </div>
                  <div className="mt-3 flex items-center gap-2 capitalize text-slate-300">
                    {txn.type === "deposit" ? (
                      <ArrowDownCircle className="text-emerald-400" size={18} />
                    ) : (
                      <ArrowUpCircle className="text-rose-400" size={18} />
                    )}
                    {txn.type.replace("_", " ")}
                  </div>
                  <p className="mt-3 text-lg font-bold text-white">₦{txn.amount.toLocaleString()}</p>
                  <p className="mt-1 text-xs text-slate-400">{new Date(txn.createdAt).toLocaleString()}</p>
                </div>
              ))}
            </div>
          </>
        )}
      </section>
    </div>
  );
}
